import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Loader2, FileSignature, AlertTriangle, CheckCircle2, Download, Info, LogIn,
  CreditCard, Clock, PartyPopper, FileText, Eye, Gauge, Camera, Upload,
} from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = '/api';

// Matches the Helios brand logo used on the staff Login + portal pages.
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';

// Guidance images for the post-sign DNO detail capture.
const MPAN_GUIDE_IMG = 'https://customer-assets-jt897jd0.emergentagent.net/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/a8svq7ox_MPAN-number-example.jpg';
const CUTOUT_GUIDE_IMG = 'https://customer-assets-jt897jd0.emergentagent.net/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/2t7kwq2z_Electric%20Meter%20Cupboard%20example.jpeg';

/**
 * Public contract-pack signing page. No login. Token-authenticated.
 * Flow: load → review PDF → sign canvas → submit → success
 *
 * Brand surface:
 *   - Logo lock-up replaces the wordmark pill at the top.
 *   - Page background uses the Helios blue gradient.
 *   - Primary signing CTA uses the blue brand colour, not generic emerald.
 *   - Customer-portal login button is surfaced on both the signing page
 *     and the post-sign confirmation card so customers can jump straight
 *     into their portal once the contract is in flight.
 */
// FastAPI 422s return `detail` as an array of objects — coerce to a string so
// it's never rendered as a React child (which would crash the page).
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

const PublicSignContract = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signature, setSignature] = useState('');
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  // Full-PDF viewer is lazy-loaded on demand — customers first see a fast
  // first-page preview image so mobile / slow connections aren't staring at a
  // blank 10 MB+ iframe.
  const [showFullPdf, setShowFullPdf] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const [firstPageSrc, setFirstPageSrc] = useState('');

  // Post-sign deposit flow
  const [depositInvoice, setDepositInvoice] = useState(null);
  const [depositPaid, setDepositPaid] = useState(false);
  const [payLaterChosen, setPayLaterChosen] = useState(false);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [showPayLaterPrompt, setShowPayLaterPrompt] = useState(false);

  // Post-sign DNO detail capture (MPAN + meter cupboard / cut-out photo).
  // Both are optional and skippable; they feed the DNO application.
  const [dnoStatus, setDnoStatus] = useState({ has_mpan: false, mpan: '', has_cutout: false });
  const [mpanInput, setMpanInput] = useState('');
  const [mpanSaving, setMpanSaving] = useState(false);
  const [mpanScanning, setMpanScanning] = useState(false);
  const [mpanScanNote, setMpanScanNote] = useState('');
  const [mpanSkipped, setMpanSkipped] = useState(false);
  const [cutoutUploading, setCutoutUploading] = useState(false);
  const [cutoutSkipped, setCutoutSkipped] = useState(false);

  const looksLikeMpan = (v) => {
    const digits = (v || '').replace(/\s/g, '');
    return /^\d{13}$/.test(digits) || /^\d{21}$/.test(digits) || /S\d{2}/i.test(v || '');
  };

  const refreshDnoStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/public/contract-pack/${token}/dno-status`);
      setDnoStatus(data);
      if (data.mpan) setMpanInput(data.mpan);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    if (signed) refreshDnoStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signed]);

  const submitMpan = async () => {
    const mpan = (mpanInput || '').trim();
    if (!mpan) return;
    if (!looksLikeMpan(mpan) && !window.confirm("That doesn't look like a typical MPAN (usually 13 or 21 digits, starting with an S box). Save it anyway?")) return;
    setMpanSaving(true);
    try {
      await axios.post(`${API}/public/contract-pack/${token}/mpan`, { mpan });
      setDnoStatus((s) => ({ ...s, has_mpan: true, mpan }));
    } catch (e) {
      setError(errText(e, 'Could not save your MPAN. Please try again.'));
    } finally {
      setMpanSaving(false);
    }
  };

  const scanBillForMpan = async (file) => {
    if (!file) return;
    setMpanScanning(true);
    setError('');
    setMpanScanNote('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/public/contract-pack/${token}/mpan-ocr`, fd);
      const bits = [];
      if (data?.mpan) { setMpanInput(data.mpan); bits.push('MPAN filled in below'); }
      if (data?.annual_usage_kwh || data?.monthly_usage_kwh) bits.push('usage saved');
      if (data?.bill_saved) bits.push('bill saved to your file');
      if (bits.length) {
        setMpanScanNote(`Bill scanned — ${bits.join(', ')}.${data?.mpan ? ' Please double-check the MPAN.' : ''}`);
        if (!data?.mpan) setError("We couldn't read the MPAN from that photo — please type it in.");
      } else {
        setError("We couldn't read the MPAN from that photo — please type it in.");
      }
    } catch {
      setError("We couldn't read the MPAN from that photo — please type it in.");
    } finally {
      setMpanScanning(false);
    }
  };

  const uploadCutout = async (file) => {
    if (!file) return;
    setCutoutUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/public/contract-pack/${token}/cutout`, fd);
      setDnoStatus((s) => ({ ...s, has_cutout: true }));
    } catch (e) {
      setError(errText(e, 'Could not upload your photo. Please try again.'));
    } finally {
      setCutoutUploading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/public/contract-pack/${token}`);
      setPack(r.data);
      setTypedName(r.data.customer_name || '');
      setDepositInvoice(r.data.deposit_invoice || null);
      setDepositPaid(!!r.data.deposit_paid);
    } catch (e) {
      // If the pack is already signed (409), fall back to the deposit-info
      // endpoint so returning customers still land on the deposit screen.
      if (e.response?.status === 409) {
        try {
          const d = await axios.get(`${API}/public/contract-pack/${token}/deposit-info`);
          setTypedName(d.data.customer_name || '');
          setSigned(true);
          setDepositInvoice(d.data.deposit_invoice || null);
          setDepositPaid(!!d.data.deposit_paid);
        } catch {
          setError('This signing link is invalid or has expired');
        }
      } else {
        setError(e.response?.data?.detail || 'This signing link is invalid or has expired');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Fetch the first-page preview as a blob so we can also read the X-Page-Count
  // header (sets customer expectations + supports "read the full pack" compliance).
  useEffect(() => {
    if (!pack || signed) return;
    let objectUrl;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/contract-pack/${token}/first-page.png`, { responseType: 'blob' });
        const count = parseInt(res.headers['x-page-count'], 10);
        if (!Number.isNaN(count)) setPageCount(count);
        objectUrl = URL.createObjectURL(res.data);
        setFirstPageSrc(objectUrl);
      } catch {
        setPreviewError(true);
      }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    /* eslint-disable-next-line */
  }, [pack, signed, token]);

  // Handle the Stripe redirect back onto this page (?deposit=success|cancel)
  useEffect(() => {
    const depositState = searchParams.get('deposit');
    const sessionId = searchParams.get('session_id');
    if (!depositState) return;
    setSigned(true);

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('deposit');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    if (depositState === 'success' && sessionId) {
      setVerifyingPayment(true);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(
            `${API}/public/contract-pack/${token}/payment-status/${sessionId}`,
          );
          if (data.payment_status === 'paid') {
            setDepositPaid(true);
            setVerifyingPayment(false);
            clearParams();
            return;
          }
        } catch { /* keep polling */ }
        if (attempts >= 8) {
          setVerifyingPayment(false);
          clearParams();
          return;
        }
        setTimeout(poll, 1800);
      };
      poll();
    } else {
      // cancelled — re-fetch outstanding deposit so we can offer it again
      axios.get(`${API}/public/contract-pack/${token}/deposit-info`)
        .then((d) => { setDepositInvoice(d.data.deposit_invoice || null); setDepositPaid(!!d.data.deposit_paid); })
        .catch(() => {});
      clearParams();
    }
    /* eslint-disable-next-line */
  }, [searchParams, token]);

  const submit = async () => {
    if (!signature) {
      setError('Please sign in the box above before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/public/contract-pack/${token}/sign`, {
        signature_data_url: signature,
        typed_name: typedName,
      });
      setDepositInvoice(data.deposit_invoice || null);
      setSigned(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit your signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const payDepositNow = async () => {
    setPayingDeposit(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/public/contract-pack/${token}/pay-deposit`);
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the payment. Please try again.');
      setPayingDeposit(false);
    }
  };

  const choosePayLater = async () => {
    setShowPayLaterPrompt(false);
    setPayingDeposit(true);
    try {
      await axios.post(`${API}/public/contract-pack/${token}/pay-later`);
      setPayLaterChosen(true);
    } catch {
      setPayLaterChosen(true); // still confirm to the customer; admins are the fallback
    } finally {
      setPayingDeposit(false);
    }
  };

  const payFromPrompt = async () => {
    setShowPayLaterPrompt(false);
    await payDepositNow();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800">
        <Loader2 className="w-8 h-8 animate-spin text-white/80" />
      </div>
    );
  }

  if (error && !pack) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-6" data-testid="sign-contract-error">
        <Card className="max-w-md w-full border-rose-200">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-1">Cannot open this link</h1>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    const amountStr = depositInvoice
      ? `£${Number(depositInvoice.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';
    const depositPending = !verifyingPayment && !depositPaid && !payLaterChosen && !!depositInvoice;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-6 flex items-center justify-center" data-testid="sign-contract-success">
        <div className="max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <img
              src={LOGO_URL}
              alt="Helios Electrical and Solar"
              className="h-12 w-auto drop-shadow-md"
              data-testid="sign-contract-success-logo"
            />
          </div>
          <Card className="border-emerald-200 shadow-2xl">
            <CardContent className="p-8 text-center space-y-4">
              {depositPending ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-emerald-600" data-testid="signed-confirm-inline">
                    <CheckCircle2 className="w-6 h-6" />
                    <span className="text-sm font-semibold">Contract signed</span>
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">One last step — pay your deposit</h1>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Pay your deposit now to secure your installation slot. We can't place any pre-orders
                    or progress your grid connection until it's received.
                  </p>
                  {typedName ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Signed by {typedName}</Badge>
                  ) : null}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                  <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Thank you!</h1>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Your contract has been signed successfully. Helios Electrical &amp; Solar will now counter-sign
                    and you'll receive a fully-executed copy by email shortly.
                  </p>
                  {typedName ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Signed by {typedName}</Badge>
                  ) : null}
                </>
              )}

              {/* ── Deposit payment window ── */}
              {verifyingPayment && (
                <div className="pt-4 border-t border-slate-100 flex items-center justify-center gap-2 text-sm text-slate-600" data-testid="deposit-verifying">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> Confirming your payment…
                </div>
              )}

              {!verifyingPayment && depositPaid && (
                <div className="pt-4 border-t border-slate-100 space-y-2" data-testid="deposit-paid-confirm">
                  <PartyPopper className="w-8 h-8 text-emerald-500 mx-auto" />
                  <p className="text-sm font-semibold text-emerald-700">Deposit received — thank you!</p>
                  <p className="text-xs text-slate-500">Your payment has been confirmed. We'll be in touch about your installation.</p>
                </div>
              )}

              {!verifyingPayment && !depositPaid && payLaterChosen && (
                <div className="pt-4 border-t border-slate-100 space-y-2" data-testid="deposit-pay-later-confirm">
                  <Clock className="w-8 h-8 text-amber-500 mx-auto" />
                  <p className="text-sm font-semibold text-slate-800">No problem — pay later</p>
                  <p className="text-xs text-slate-500">
                    We've let the team know. Your deposit invoice{depositInvoice?.number ? ` (${depositInvoice.number})` : ''} is
                    available any time in your customer portal.
                  </p>
                </div>
              )}

              {!verifyingPayment && !depositPaid && !payLaterChosen && depositInvoice && (
                <div className="pt-4 border-t border-slate-100 space-y-3 text-left" data-testid="deposit-payment-window">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <p className="text-sm font-semibold text-slate-900">Pay your deposit</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Deposit due{depositInvoice.number ? ` · ${depositInvoice.number}` : ''}</p>
                      <p className="text-xl font-bold text-slate-900" data-testid="deposit-amount">{amountStr}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">Outstanding</Badge>
                  </div>
                  {error && (
                    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {error}
                    </div>
                  )}
                  <Button
                    onClick={payDepositNow}
                    disabled={payingDeposit}
                    className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-md"
                    data-testid="pay-deposit-now-btn"
                  >
                    {payingDeposit ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to secure payment…</>
                    ) : (
                      <><CreditCard className="w-4 h-4 mr-2" /> Pay {amountStr} deposit now</>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowPayLaterPrompt(true)}
                    disabled={payingDeposit}
                    variant="outline"
                    className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="pay-deposit-later-btn"
                  >
                    <Clock className="w-4 h-4 mr-2" /> I'll pay later
                  </Button>
                  <p className="text-[10px] text-slate-400 text-center">Secure payment powered by Stripe.</p>
                </div>
              )}

              {/* ── DNO details: MPAN + cut-out photo (after deposit) ── */}
              {!depositPending && !verifyingPayment && (
                <div className="pt-4 border-t border-slate-100 space-y-4 text-left" data-testid="dno-detail-capture">
                  <p className="text-xs text-slate-500">
                    Two quick things help us get your grid (DNO) application in fast. Both are optional — you can add them later in your portal.
                  </p>

                  {/* MPAN */}
                  {dnoStatus.has_mpan ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="mpan-done">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div className="text-sm text-emerald-800">MPAN saved <span className="font-mono">{dnoStatus.mpan}</span></div>
                    </div>
                  ) : !mpanSkipped ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="mpan-card">
                      <div className="flex items-center gap-2">
                        <Gauge className="w-5 h-5 text-blue-600" />
                        <p className="text-sm font-semibold text-slate-900">Your MPAN (electricity supply number)</p>
                      </div>
                      <p className="text-xs text-slate-500">
                        Find this at the top of any electricity bill — the "Supply Number" / S box (like the picture below).
                      </p>
                      <img src={MPAN_GUIDE_IMG} alt="Where to find your MPAN on an electricity bill" className="w-full rounded border border-slate-200" data-testid="mpan-guide-img" />
                      <label className="block">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => scanBillForMpan(e.target.files?.[0])}
                          data-testid="mpan-scan-input"
                        />
                        <span className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer border ${mpanScanning ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}>
                          {mpanScanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading your bill…</> : <><Camera className="w-4 h-4" /> Scan my bill to fill it in</>}
                        </span>
                      </label>
                      {mpanScanNote && (
                        <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="mpan-scan-note">
                          <CheckCircle2 className="w-3.5 h-3.5" /> {mpanScanNote}
                        </p>
                      )}
                      <Input
                        value={mpanInput}
                        onChange={(e) => setMpanInput(e.target.value)}
                        placeholder="e.g. 20 0001 2345 678"
                        className="font-mono"
                        data-testid="mpan-input"
                      />
                      <div className="flex gap-2">
                        <Button onClick={submitMpan} disabled={mpanSaving || !mpanInput.trim()} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="mpan-save-btn">
                          {mpanSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save MPAN
                        </Button>
                        <Button variant="outline" onClick={() => setMpanSkipped(true)} className="border-slate-300 text-slate-600" data-testid="mpan-skip-btn">Skip for now</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between" data-testid="mpan-skipped">
                      <div className="text-xs text-amber-800">MPAN skipped — your DNO application will wait until it's provided.</div>
                      <Button size="sm" variant="ghost" onClick={() => setMpanSkipped(false)} className="text-amber-700" data-testid="mpan-unskip-btn">Add now</Button>
                    </div>
                  )}

                  {/* Cut-out / meter cupboard photo */}
                  {dnoStatus.has_cutout ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="cutout-done">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div className="text-sm text-emerald-800">Meter cupboard photo received — thank you!</div>
                    </div>
                  ) : !cutoutSkipped ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="cutout-card">
                      <div className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-blue-600" />
                        <p className="text-sm font-semibold text-slate-900">Photo of your meter cupboard (cut-out fuse)</p>
                      </div>
                      <p className="text-xs text-slate-500">
                        Please take a clear photo of your electricity meter cupboard showing the main cut-out fuse — like the example below.
                      </p>
                      <img src={CUTOUT_GUIDE_IMG} alt="Example photo of a meter cupboard / cut-out fuse" className="w-full rounded border border-slate-200 max-h-56 object-cover" data-testid="cutout-guide-img" />
                      <label className="block">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => uploadCutout(e.target.files?.[0])}
                          data-testid="cutout-file-input"
                        />
                        <span className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer ${cutoutUploading ? 'bg-slate-300 text-slate-600' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                          {cutoutUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Take / upload photo</>}
                        </span>
                      </label>
                      <Button variant="outline" onClick={() => setCutoutSkipped(true)} className="w-full border-slate-300 text-slate-600" data-testid="cutout-skip-btn">Skip for now</Button>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between" data-testid="cutout-skipped">
                      <div className="text-xs text-amber-800">Photo skipped — your DNO application will wait until it's provided.</div>
                      <Button size="sm" variant="ghost" onClick={() => setCutoutSkipped(false)} className="text-amber-700" data-testid="cutout-unskip-btn">Add now</Button>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Track your installation, view documents, and chat with us:</p>
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
                  data-testid="success-portal-login-btn"
                >
                  <Link to="/portal/login">
                    <LogIn className="w-4 h-4 mr-2" /> Log in to your customer portal
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <AlertDialog open={showPayLaterPrompt} onOpenChange={setShowPayLaterPrompt}>
          <AlertDialogContent data-testid="pay-later-prompt-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Before you go — your deposit
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2 text-left">
                <span className="block">
                  Please note we <strong>cannot place any pre-orders or progress your DNO (grid
                  connection) application until your deposit{depositInvoice?.amount ? ` of ${amountStr}` : ''} is paid</strong>.
                </span>
                <span className="block">
                  Paying now only takes a minute and keeps your installation moving. Would you like
                  to pay before you close?
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={(e) => { e.preventDefault(); choosePayLater(); }}
                data-testid="pay-later-confirm-btn"
              >
                No, I'll pay later
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); payFromPrompt(); }}
                className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
                data-testid="pay-later-pay-now-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" /> Pay {amountStr} now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  const pdfUrl = `${API}/public/contract-pack/${token}/preview.pdf`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 py-8 px-4" data-testid="public-sign-contract-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header — logo lock-up replaces the wordmark pill */}
        <div className="text-center">
          <div className="flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="Helios Electrical and Solar"
              className="h-14 w-auto drop-shadow-md"
              data-testid="sign-contract-logo"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-4 font-['Manrope'] tracking-tight">
            Your Contract is Ready to Sign
          </h1>
          <p className="text-sm text-sky-100 mt-1">
            For: <strong className="text-white">{pack.customer_name}</strong>{pack.customer_address ? ` · ${pack.customer_address}` : ''}
          </p>
          {/* Customer-portal login link at the top — useful for customers who
              want to jump back into the portal mid-review. */}
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="mt-3 text-sky-100 hover:text-white hover:bg-white/10 border border-white/20"
            data-testid="sign-contract-portal-login-btn"
          >
            <Link to="/portal/login">
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Customer portal login
            </Link>
          </Button>
        </div>

        {/* PDF — wrapped in a white card on the dark backdrop */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
            <CardTitle className="text-base flex items-center gap-2 text-blue-900">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Step 1 — Review the Contract Pack
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {!showFullPdf ? (
              <div className="space-y-3" data-testid="contract-preview-mode">
                {previewError ? (
                  <div className="w-full rounded-md border-2 border-blue-100 bg-slate-50 p-8 text-center" data-testid="preview-fallback">
                    <FileText className="w-10 h-10 text-blue-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      Preview unavailable — please open or download the full contract pack below.
                    </p>
                  </div>
                ) : !firstPageSrc ? (
                  <div className="w-full rounded-md border-2 border-blue-100 bg-slate-50 p-12 flex flex-col items-center justify-center gap-2" data-testid="preview-loading">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <p className="text-xs text-slate-500">Loading your contract preview…</p>
                  </div>
                ) : (
                  <div className="relative w-full rounded-md overflow-hidden border-2 border-blue-100 bg-slate-50">
                    <img
                      src={firstPageSrc}
                      alt="Contract pack — first page preview"
                      className="w-full h-auto block"
                      loading="eager"
                      data-testid="contract-first-page-img"
                    />
                    <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-white via-white/85 to-transparent flex items-end justify-center pb-2.5 pointer-events-none">
                      <span className="text-xs font-medium text-slate-500" data-testid="preview-page-hint">
                        {pageCount
                          ? `Page 1 of ${pageCount} — open the full pack to read every page`
                          : 'Page 1 preview — open the full pack to read every page'}
                      </span>
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => setShowFullPdf(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-md"
                  data-testid="view-full-pack-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {pageCount ? `Read the full ${pageCount}-page contract` : 'Read the full contract pack'}
                </Button>
              </div>
            ) : (
              <div className="relative" data-testid="contract-full-pdf-mode">
                {!iframeLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50 border-2 border-blue-100 rounded-md z-10" data-testid="iframe-loading-overlay">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <p className="text-xs text-slate-500">Loading the full contract pack…</p>
                  </div>
                )}
                <iframe
                  src={pdfUrl}
                  onLoad={() => setIframeLoaded(true)}
                  className="w-full h-[60vh] border-2 border-blue-100 rounded-md bg-slate-50"
                  title="Helios contract pack"
                  data-testid="public-contract-iframe"
                />
              </div>
            )}
            {/* Loading-hint comment, just below the PDF preview, so customers
                on slow connections know what to do if the iframe is still
                showing a spinner / blank for too long. */}
            <div
              className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md p-2.5"
              data-testid="iframe-loading-hint"
            >
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
              <p className="leading-relaxed">
                The full contract pack can be large. If the inline viewer is slow on your
                connection, use the <strong>Download</strong> button below to open it locally — then
                come back here to sign.
              </p>
            </div>
            <Button
              variant="outline"
              asChild
              className="w-full sm:w-auto border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <a href={pdfUrl} target="_blank" rel="noreferrer" download="HeliosContractPack.pdf">
                <Download className="w-4 h-4 mr-2" /> Download a copy for your records
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Sign */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
            <CardTitle className="text-base flex items-center gap-2 text-blue-900">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Step 2 — Sign &amp; Accept
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="text-xs text-slate-600 font-medium" htmlFor="typed-name">Your full name</label>
              <Input
                id="typed-name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Full name as it appears on the contract"
                className="mt-1 border-blue-200 focus:border-blue-400 focus:ring-blue-200"
                data-testid="typed-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium block mb-1">Sign in the box below</label>
              <SignaturePad onChange={setSignature} height={180} dataTestId="contract-signature-pad" />
              <p className="text-[10px] text-slate-400 mt-1">Use your finger on touchscreens, or a mouse on desktops.</p>
            </div>
            {error && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            {depositInvoice && !depositPaid && (
              <div
                className="flex items-start gap-2 text-[11px] text-blue-900 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-md p-3 leading-relaxed"
                data-testid="pre-sign-deposit-notice"
              >
                <CreditCard className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                <p>
                  A deposit of{' '}
                  <strong>
                    £{Number(depositInvoice.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>{' '}
                  is due to secure your installation. As soon as you sign, you'll be able to pay it
                  securely on the next screen.
                </p>
              </div>
            )}
            <div className="text-[11px] text-slate-600 bg-blue-50 border border-blue-200 rounded p-3 leading-relaxed">
              By signing, you confirm that you have read the full contract pack above (including the Right to Cancel and Cancellation Form), that you accept the terms and conditions set out, and that you authorise Helios Electrical &amp; Solar to proceed with the installation as described.
            </div>
            <Button
              onClick={submit}
              disabled={submitting || !signature || !typedName}
              className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-md"
              data-testid="public-sign-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> I agree &amp; Sign Contract</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer brand strip */}
        <p className="text-center text-[11px] text-sky-200/70 pt-2">
          Helios Electrical &amp; Solar · MCS-accredited installer · helioses.co.uk ·{' '}
          <Link to="/privacy-policy" className="underline hover:text-white" data-testid="privacy-policy-link">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PublicSignContract;
